/* ==========================================================================
   Lauren Baker — theme.js (vanilla, no dependencies)
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveal ------------------------------------------------------ */
  function initReveal() {
    var targets = document.querySelectorAll('.reveal');
    if (!targets.length) return;
    if (REDUCED_MOTION || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ---- Mobile menu ------------------------------------------------------------ */
  function initMobileMenu() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var menu = document.getElementById('mobile-menu');
    if (!toggle || !menu) return;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', String(open));
      menu.classList.toggle('is-open', open);
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        menu.querySelectorAll('.mobile-menu__link').forEach(function (link, i) {
          link.style.transitionDelay = (0.06 * i + 0.1) + 's';
        });
      }
    }
    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setOpen(false);
    });
  }

  /* ---- Nav dropdowns --------------------------------------------------------- */
  function initNavDropdowns() {
    // Desktop: hover is handled in CSS; add click + keyboard for touch/a11y
    var items = document.querySelectorAll('[data-nav-dropdown]');
    items.forEach(function (item) {
      var parentLink = item.querySelector('.site-nav__link--parent');
      var caretBtn = item.querySelector('[data-nav-dropdown-toggle]');

      function setOpen(open) {
        item.classList.toggle('is-open', open);
        if (parentLink) parentLink.setAttribute('aria-expanded', String(open));
        if (caretBtn) caretBtn.setAttribute('aria-expanded', String(open));
      }
      // Caret toggles without following the parent link
      if (caretBtn) {
        caretBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!item.classList.contains('is-open'));
        });
      }
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { setOpen(false); if (parentLink) parentLink.focus(); }
      });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) setOpen(false);
      });
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-nav-dropdown]')) {
        items.forEach(function (item) { item.classList.remove('is-open'); });
      }
    });

    // Mobile: accordion expand
    document.querySelectorAll('[data-mobile-expand]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var group = btn.closest('[data-mobile-group]');
        var open = group.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  }

  /* ---- Hero slider --------------------------------------------------------------- */
  function initHeroSliders() {
    document.querySelectorAll('[data-hero-slider]').forEach(function (slider) {
      var slides = slider.querySelectorAll('.hero__slide');
      if (slides.length < 2) return;
      var dots = slider.querySelectorAll('.hero__dot');
      var interval = parseInt(slider.dataset.autoplaySpeed, 10) || 6000;
      var current = 0;
      var timer = null;

      function show(index) {
        slides[current].classList.remove('is-active');
        if (dots[current]) dots[current].setAttribute('aria-current', 'false');
        current = (index + slides.length) % slides.length;
        slides[current].classList.add('is-active');
        if (dots[current]) dots[current].setAttribute('aria-current', 'true');
      }
      function play() {
        if (REDUCED_MOTION) return;
        timer = window.setInterval(function () { show(current + 1); }, interval);
      }
      function stop() { if (timer) window.clearInterval(timer); }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { stop(); show(i); play(); });
      });
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', play);
      play();
    });
  }

  /* ---- Lightbox -------------------------------------------------------------------- */
  function initLightbox() {
    var lightbox = document.getElementById('lightbox');
    if (!lightbox) return;
    var image = lightbox.querySelector('[data-lightbox-image]');
    var items = [];
    var index = 0;
    var lastFocus = null;

    function open(group, i) {
      items = group;
      index = i;
      lastFocus = document.activeElement;
      render();
      lightbox.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      lightbox.querySelector('[data-lightbox-close]').focus();
    }
    function close() {
      lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }
    function render() {
      var item = items[index];
      image.src = item.src;
      image.alt = item.alt || '';
    }
    function step(dir) {
      index = (index + dir + items.length) % items.length;
      render();
    }

    document.querySelectorAll('[data-lightbox-group]').forEach(function (group) {
      var triggers = Array.prototype.slice.call(group.querySelectorAll('[data-lightbox-src]'));
      var data = triggers.map(function (t) {
        return { src: t.dataset.lightboxSrc, alt: t.dataset.lightboxAlt || '' };
      });
      triggers.forEach(function (trigger, i) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          open(data, i);
        });
      });
    });

    lightbox.querySelector('[data-lightbox-close]').addEventListener('click', close);
    lightbox.querySelector('[data-lightbox-prev]').addEventListener('click', function () { step(-1); });
    lightbox.querySelector('[data-lightbox-next]').addEventListener('click', function () { step(1); });
    lightbox.addEventListener('click', function (e) { if (e.target === lightbox) close(); });
    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    });
  }

  /* ---- Video facade (no third-party branding until click) ---------------------------- */
  function initVideoFacades() {
    document.querySelectorAll('[data-video-embed]').forEach(function (card) {
      var play = card.querySelector('[data-video-play]');
      if (!play) return;
      play.addEventListener('click', function () {
        var frame = card.querySelector('.video-card__frame');
        var iframe = document.createElement('iframe');
        iframe.src = card.dataset.videoEmbed + (card.dataset.videoEmbed.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.setAttribute('allowfullscreen', '');
        iframe.title = card.dataset.videoTitle || 'Video';
        frame.innerHTML = '';
        frame.appendChild(iframe);
      });
    });
  }

  /* ---- Product gallery ------------------------------------------------------------------ */
  function initProductGallery() {
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-gallery-main]');
    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        main.src = thumb.dataset.galleryFull;
        main.alt = thumb.querySelector('img').alt;
        gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
          t.setAttribute('aria-current', 'false');
        });
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  /* ---- Collection filter bar ------------------------------------------------ */
  function initFilterBar() {
    var bar = document.querySelector('[data-filter-bar]');
    if (!bar) return;
    var dropdowns = bar.querySelectorAll('[data-filter-dropdown]');

    dropdowns.forEach(function (dd) {
      var toggle = dd.querySelector('.filter-dropdown__toggle');
      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = dd.classList.contains('is-open');
        dropdowns.forEach(function (other) {
          other.classList.remove('is-open');
          other.querySelector('.filter-dropdown__toggle').setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          dd.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-filter-dropdown]')) {
        dropdowns.forEach(function (dd) {
          dd.classList.remove('is-open');
          dd.querySelector('.filter-dropdown__toggle').setAttribute('aria-expanded', 'false');
        });
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        dropdowns.forEach(function (dd) { dd.classList.remove('is-open'); });
      }
    });

    var form = bar.querySelector('[data-filter-form]');
    if (form) {
      form.addEventListener('change', function (e) {
        if (e.target.type === 'checkbox' || e.target.type === 'radio') {
          form.submit();
        }
      });
      form.addEventListener('submit', function () {
        // Drop empty inputs so URLs stay clean
        form.querySelectorAll('input[name]').forEach(function (input) {
          if ((input.type === 'number' || input.type === 'text') && input.value === '') {
            input.disabled = true;
          }
        });
      });
    }
  }

/* ---- Image carousel -------------------------------------------------------- */
  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-carousel-track]');
      var slides = Array.prototype.slice.call(track.children);
      if (slides.length < 2) return;
      var dotsWrap = carousel.querySelector('[data-carousel-dots]');
      var prev = carousel.querySelector('[data-carousel-prev]');
      var next = carousel.querySelector('[data-carousel-next]');
      var configuredPerView = parseInt(carousel.dataset.perView, 10) || 1;
      var autoplay = carousel.dataset.autoplay === 'true';
      var speed = parseInt(carousel.dataset.autoplaySpeed, 10) || 5000;
      var page = 0;
      var timer = null;
      var pageCount = 1;

      function perView() {
        return window.matchMedia('(min-width: 1024px)').matches ? configuredPerView : 1;
      }

      function buildDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        for (var i = 0; i < pageCount; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
          (function (idx) {
            dot.addEventListener('click', function () { go(idx); restart(); });
          })(i);
          dotsWrap.appendChild(dot);
        }
        dotsWrap.hidden = pageCount < 2;
      }

      function go(p) {
        page = (p + pageCount) % pageCount;
        var pv = perView();
        var maxOffset = Math.max(0, slides.length * (100 / pv) - 100);
        var offset = Math.min(page * 100, maxOffset);
        track.style.transform = 'translateX(-' + offset + '%)';
        if (dotsWrap) {
          Array.prototype.forEach.call(dotsWrap.children, function (d, di) {
            d.setAttribute('aria-current', di === page ? 'true' : 'false');
          });
        }
        var single = pageCount < 2;
        if (prev) prev.style.display = single ? 'none' : '';
        if (next) next.style.display = single ? 'none' : '';
      }

      function layout() {
        pageCount = Math.ceil(slides.length / perView());
        if (page > pageCount - 1) page = pageCount - 1;
        buildDots();
        go(page);
      }

      function restart() { if (autoplay && !REDUCED_MOTION) { stop(); timer = setInterval(function () { go(page + 1); }, speed); } }
      function stop() { if (timer) clearInterval(timer); }

      if (prev) prev.addEventListener('click', function () { go(page - 1); restart(); });
      if (next) next.addEventListener('click', function () { go(page + 1); restart(); });

      var startX = 0, dragging = false;
      var vp = carousel.querySelector('[data-carousel-viewport]');
      vp.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; dragging = true; }, { passive: true });
      vp.addEventListener('touchend', function (e) {
        if (!dragging) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) { go(page + (dx < 0 ? 1 : -1)); restart(); }
        dragging = false;
      });
      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', restart);

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(layout, 150);
      });

      layout();
      restart();
    });
  }
  /* ---- Page fade transitions --------------------------------------------------------------- */
  function initPageTransitions() {
    if (REDUCED_MOTION) return;
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      var url = new URL(link.href, window.location.origin);
      var isInternal = url.origin === window.location.origin;
      var isSamePage = url.pathname === window.location.pathname && url.hash;
      if (!isInternal || isSamePage || link.target === '_blank' || e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      document.body.classList.add('is-leaving');
      window.setTimeout(function () { window.location.href = link.href; }, 200);
    });
    window.addEventListener('pageshow', function () {
      document.body.classList.remove('is-leaving');
    });
  }

  /* ---- Newsletter popup ------------------------------------------------------ */
  function initNewsletterPopup() {
    var popup = document.querySelector('[data-newsletter-popup]');
    if (!popup) return;
    var KEY = 'lb-nl-popup';
    var freq = popup.dataset.frequency;
    var trigger = popup.dataset.trigger;
    var delay = parseInt(popup.dataset.delay, 10) || 6000;
    var lastFocus = null;
    var shown = false;

    function dismissed() {
      try {
        var v = window.localStorage.getItem(KEY);
        if (!v) return false;
        if (v === 'never') return true;
        if (v === 'session') return window.sessionStorage.getItem(KEY) === '1';
        var until = parseInt(v, 10);
        return !isNaN(until) && Date.now() < until;
      } catch (e) { return false; }
    }
    function remember() {
      try {
        if (freq === 'never') window.localStorage.setItem(KEY, 'never');
        else if (freq === 'session') { window.localStorage.setItem(KEY, 'session'); window.sessionStorage.setItem(KEY, '1'); }
        else { window.localStorage.setItem(KEY, String(Date.now() + parseInt(freq, 10) * 86400000)); }
      } catch (e) { /* private mode */ }
    }
    function open() {
      if (shown || dismissed()) return;
      shown = true;
      lastFocus = document.activeElement;
      popup.hidden = false;
      requestAnimationFrame(function () { popup.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      var f = popup.querySelector('input, button');
      if (f) f.focus();
    }
    function close() {
      popup.classList.remove('is-open');
      document.body.style.overflow = '';
      remember();
      window.setTimeout(function () { popup.hidden = true; }, 300);
      if (lastFocus) lastFocus.focus();
    }

    if (dismissed()) return;

    // If the form just submitted successfully, show it so the success message is visible
    if (popup.querySelector('.form-status')) { open(); }

    if (trigger === 'delay') {
      window.setTimeout(open, delay);
    } else if (trigger === 'scroll') {
      var onScroll = function () {
        var pct = (window.scrollY + window.innerHeight) / document.body.scrollHeight;
        if (pct > 0.5) { open(); window.removeEventListener('scroll', onScroll); }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    } else if (trigger === 'exit') {
      document.addEventListener('mouseout', function (e) {
        if (!e.relatedTarget && e.clientY < 10) open();
      });
      // mobile fallback: delay
      if (window.matchMedia('(max-width: 767px)').matches) window.setTimeout(open, delay * 1.5);
    }

    popup.querySelectorAll('[data-newsletter-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) close();
    });
  }

  /* ---- Cookie banner --------------------------------------------------------------------------- */
  function initCookieBanner() {
    var banner = document.getElementById('cookie-banner');
    if (!banner) return;
    var KEY = 'lb-cookie-consent';
    try {
      if (!window.localStorage.getItem(KEY)) banner.hidden = false;
    } catch (err) {
      banner.hidden = false;
    }
    function dismiss(value) {
      try { window.localStorage.setItem(KEY, value); } catch (err) { /* private mode */ }
      banner.hidden = true;
    }
    banner.querySelector('[data-cookie-accept]').addEventListener('click', function () { dismiss('accepted'); });
    banner.querySelector('[data-cookie-reject]').addEventListener('click', function () { dismiss('rejected'); });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initMobileMenu();
    initNavDropdowns();
    initHeroSliders();
    initLightbox();
    initVideoFacades();
    initProductGallery();
    initFilterBar();
    initCarousels();
    initPageTransitions();
    initNewsletterPopup();
    initCookieBanner();
  });
})();
